/**
 * APEX v7.3 — Constants
 *
 * Centralise les configurations statiques du projet :
 *   - Tab list (config du routing principal)
 *   - Queue filters (Solo/Flex/Normal/Tout)
 *   - Patch fallback
 *   - Constantes de temps (DAY_MS, WEEK_MS, MONTH_MS, etc.)
 *   - Etc.
 *
 * Pourquoi ici : isoler la config des composants pour la rendre
 * facile à modifier sans toucher au JSX, et préparer la factorisation
 * des tabs en composants séparés.
 */

// ============================================================
// TEMPS (constantes en millisecondes)
// ============================================================

export const SECOND_MS  = 1000;
export const MINUTE_MS  = 60 * SECOND_MS;
export const HOUR_MS    = 60 * MINUTE_MS;
export const DAY_MS     = 24 * HOUR_MS;
export const WEEK_MS    = 7 * DAY_MS;
export const MONTH_MS   = 30 * DAY_MS;   // mois "approximatif" 30j
export const QUARTER_MS = 90 * DAY_MS;
export const YEAR_MS    = 365 * DAY_MS;

import {
  BarChart3,
  Brain,
  Target,
  PieChart,
  Trophy,
  Activity,
  Download,
  Sparkles
} from 'lucide-react';

// ============================================================
// TABS
// ============================================================

/**
 * Liste des tabs principaux du Dashboard.
 *
 * Les labels sont dynamiques (avec counters) donc on les définit
 * via une factory : `buildTabsList({ sessionGamesCount, activeBlocksCount, gamesCount })`.
 */
export function buildTabsList({ sessionGamesCount = 0, activeBlocksCount = 0, gamesCount = 0 }) {
  return [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    {
      id: 'session',
      label: `Session${sessionGamesCount > 0 ? ` (${sessionGamesCount})` : ''}`,
      icon: Brain
    },
    {
      id: 'objectives',
      label: `Objectifs${activeBlocksCount > 0 ? ` (${activeBlocksCount})` : ''}`,
      icon: Target
    },
    { id: 'analytics', label: 'Analytics', icon: PieChart },
    { id: 'coaching', label: 'Coaching', icon: Sparkles },
    { id: 'champions', label: 'Champions', icon: Trophy },
    { id: 'games', label: `Games (${gamesCount})`, icon: Activity },
    { id: 'export', label: 'Export', icon: Download }
  ];
}

/**
 * IDs valides de tabs. Utile pour valider que `activeTab` est un id connu.
 */
export const TAB_IDS = ['dashboard', 'session', 'objectives', 'analytics', 'coaching', 'champions', 'games', 'export'];

// ============================================================
// QUEUE FILTERS (utilisé par GameHistorySidebar et autres)
// ============================================================

export const QUEUE_FILTERS = [
  { value: 'all', label: 'TOUT' },
  { value: 'solo', label: 'SOLO', queueIds: [420] },
  { value: 'flex', label: 'FLEX', queueIds: [440] },
  { value: 'normal', label: 'NORMAL', queueIds: [400, 430] }
];

/**
 * Mapper un queue_id Riot vers un value lisible.
 */
export function getQueueType(queueId) {
  if (!queueId) return null;
  if (queueId === 420) return 'solo';
  if (queueId === 440) return 'flex';
  if (queueId === 400 || queueId === 430) return 'normal';
  return 'other';
}

// ============================================================
// AUTO-IMPORT
// ============================================================

export const AUTO_IMPORT_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes
export const AUTO_IMPORT_MANUAL_THROTTLE_MS = 60 * 1000; // 1 minute mini entre 2 refresh manuels
export const AUTO_IMPORT_MATCH_COUNT = 5;
export const AUTO_IMPORT_QUEUE_ID = 420; // SoloQ par défaut

// ============================================================
// FILTERS
// ============================================================

export const FILTER_TYPES = ['session', 'block', 'all'];

export const DEFAULT_FILTER = { type: 'all', label: "Tout l'historique" };

// ============ DEATH TYPES ============
// Centralisé ici — était dupliqué dans App.jsx et GameFormModal.jsx
export const DEATH_TYPES = {
  investment: {
    label: 'Investissement',
    desc: 'Trade qui rapporte (kill, obj, prio)',
    textClass: 'text-emerald-400',
    borderClass: 'border-emerald-400/30',
    borderLeftClass: 'border-emerald-400/50',
  },
  position: {
    label: 'Position',
    desc: 'Placement TF, surextend',
    textClass: 'text-amber-400',
    borderClass: 'border-amber-400/30',
    borderLeftClass: 'border-amber-400/50',
  },
  greed: {
    label: 'Greed',
    desc: 'CS de trop, recall raté, overstay',
    textClass: 'text-rose-400',
    borderClass: 'border-rose-400/30',
    borderLeftClass: 'border-rose-400/50',
  },
};
